import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { buildAdminIntakeHubPath } from '../adminJourneyRouting';
import type { IntakeFleetOrganization, IntakeFleetResponse } from '../types';

import { EhrOrganizationStatus } from './ehr/EhrFleetSummary';
import { IntakePill } from './shell/IntakePill';
import { footnoteSx, metaSx, tableHeadSx, wf } from './shell/tokens';
import { GoLiveStatus } from './GoLiveStatus';
import { useGoLiveLoadWindow } from './useGoLiveLoadWindow';

const SERVICE_LABELS: Record<string, string> = {
  jotbill: 'JotBill',
  jotcred: 'JotCred',
  jotsite: 'JotSite',
};

export type FleetTrack = 'overview' | 'credentialing' | 'ehr-import';

const headCellSx = { ...tableHeadSx, borderBottom: `1px solid ${wf.lineSoft}`, py: '11px' };
const bodyCellSx = {
  borderBottom: `1px solid ${wf.lineSoft}`,
  py: '14px',
  verticalAlign: 'top',
  color: wf.body,
};
const tableWrapSx = {
  border: `1px solid ${wf.line}`,
  borderRadius: '10px',
  overflow: 'hidden',
  backgroundColor: wf.surface,
};

function credentialingState(row: IntakeFleetOrganization) {
  const counts = row.credentialing;
  if (!counts) return { label: 'Records unavailable', tone: 'slate' as const };
  const total = counts.total ?? 0;
  if (!total) return { label: 'Not started', tone: 'slate' as const };
  if ((counts.complete ?? 0) >= total) return { label: 'Complete', tone: 'green' as const };
  if ((counts.complete ?? 0) > 0 || (counts.in_progress ?? 0) > 0)
    return { label: 'In progress', tone: 'indigo' as const };
  return { label: 'Not started', tone: 'slate' as const };
}

function PracticeCell({
  row,
  canVerify,
  verificationPath,
}: {
  row: IntakeFleetOrganization;
  canVerify: boolean;
  verificationPath: string;
}) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
        {row.organization_name}
      </Typography>
      <Typography sx={metaSx}>
        {row.selected_services == null
          ? 'Selection unknown'
          : row.selected_services.length
            ? row.selected_services.map(service => SERVICE_LABELS[service] ?? service).join(', ')
            : 'No services selected'}
      </Typography>
      {!canVerify ? (
        <Typography sx={metaSx}>Admin access required</Typography>
      ) : row.verification ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start', pt: '2px' }}>
          <IntakePill
            tone={
              row.verification.state === 'verified'
                ? 'green'
                : row.verification.state === 'awaiting'
                  ? 'amber'
                  : 'slate'
            }
            label={
              row.verification.state === 'verified'
                ? 'Verified'
                : row.verification.state === 'awaiting'
                  ? 'Awaiting verification'
                  : 'Incomplete'
            }
          />
          <Typography sx={metaSx}>
            {row.verification.provider_counts.verified} of {row.verification.provider_counts.total}{' '}
            roster providers verified
          </Typography>
          <Button
            size="small"
            component={Link}
            to={verificationPath}
            sx={{ px: 0, textTransform: 'none', fontWeight: 700 }}
          >
            Review
          </Button>
        </Stack>
      ) : (
        <Typography sx={metaSx}>No eligible verification evidence</Typography>
      )}
    </Stack>
  );
}

function CredentialingCard({
  row,
  recordsPath,
}: {
  row: IntakeFleetOrganization;
  recordsPath: string;
}) {
  const [open, setOpen] = useState(false);
  const counts = row.credentialing;
  const total = counts?.total ?? 0;
  const complete = counts?.complete ?? 0;
  const inProgress = counts?.in_progress ?? 0;
  const notStarted = counts?.not_started ?? Math.max(0, total - complete - inProgress);
  const state = credentialingState(row);
  const segments = [
    { n: complete, color: wf.green },
    { n: inProgress, color: wf.blue },
    { n: notStarted, color: wf.track },
  ].filter(segment => segment.n > 0);
  return (
    <Box sx={{ ...tableWrapSx }}>
      {/* The records link sits beside the toggle, never inside it, so opening them skips the expand. */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ButtonBase
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 20px',
            flexWrap: 'wrap',
            textAlign: 'left',
            justifyContent: 'flex-start',
          }}
        >
          <ChevronRightIcon
            sx={{
              color: wf.meta,
              fontSize: 20,
              transition: 'transform .15s',
              transform: open ? 'rotate(90deg)' : 'none',
            }}
          />
          <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: wf.ink }}>
                {row.organization_name}
              </Typography>
              <IntakePill tone={state.tone} label={state.label} />
            </Stack>
            <Typography sx={metaSx}>
              {total} recorded · {complete} complete · {inProgress} in progress · {notStarted} not
              started
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '3px', width: 180 }}>
            {segments.length ? (
              segments.map(segment => (
                <Box
                  key={segment.color}
                  sx={{
                    flex: `${segment.n} 1 0`,
                    height: '6px',
                    borderRadius: '3px',
                    backgroundColor: segment.color,
                  }}
                />
              ))
            ) : (
              <Box
                sx={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: wf.track }}
              />
            )}
          </Box>
        </ButtonBase>
        <Button
          size="small"
          component={Link}
          to={recordsPath}
          aria-label={`Open records for ${row.organization_name}`}
          sx={{ flex: '0 0 auto', mr: '20px', textTransform: 'none', fontWeight: 700 }}
        >
          Open records
        </Button>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: `1px solid ${wf.lineSoft}`, padding: '14px 20px' }}>
          <Typography sx={{ fontSize: '13px', color: wf.body, lineHeight: 1.55 }}>
            Counts describe individual provider/payer entries, not overall practice completion. The
            provider × payer records live in this organization’s Intake Hub.
          </Typography>
          {row.credentialing_updated_at && (
            <Typography sx={metaSx}>
              Last record update {new Date(row.credentialing_updated_at).toLocaleDateString()}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={recordsPath}
            sx={{ mt: 1.5, textTransform: 'none', borderRadius: '6px' }}
          >
            Open Intake Hub
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

export function StaffIntakeFleet({
  data,
  loading,
  failed,
  search,
  page,
  perPage,
  track,
  returnTo,
  canVerify,
  onSearch,
  onPage,
  onRefresh,
}: {
  data?: IntakeFleetResponse;
  loading: boolean;
  failed: boolean;
  search: string;
  page: number;
  perPage: number;
  track: FleetTrack;
  returnTo: string;
  canVerify: boolean;
  onSearch: (value: string) => void;
  onPage: (value: number) => void;
  onRefresh: () => void;
}) {
  const path = (organizationId: string, target: FleetTrack | 'billing' | 'verification') =>
    buildAdminIntakeHubPath({
      organizationId,
      stage:
        target === 'overview'
          ? 'agreements'
          : target === 'verification'
            ? 'verification'
            : 'go-live',
      statusTab: target === 'overview' || target === 'verification' ? undefined : target,
      returnTo,
    });
  const rows = data?.items ?? [];
  const organizationIds = useMemo(
    () => (track === 'overview' ? (data?.items ?? []).map(row => row.organization_id) : []),
    [data?.items, track],
  );
  const goLiveWindow = useGoLiveLoadWindow(organizationIds);
  const empty = data && rows.length === 0;
  const emptyState = empty ? (
    <Box sx={{ ...tableWrapSx, p: 4, textAlign: 'center' }}>
      <Typography>
        {data.total ? 'No organizations on this page.' : 'No organizations match this search.'}
      </Typography>
      {(data.total ?? 0) > 0 && <Button onClick={() => onPage(1)}>First page</Button>}
    </Box>
  ) : null;
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Search organizations"
          size="small"
          value={search}
          onChange={event => onSearch(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          sx={{ flex: 1, backgroundColor: wf.surface }}
        />
        <Button variant="outlined" disabled={loading} onClick={onRefresh}>
          Refresh
        </Button>
      </Stack>
      <Typography sx={{ ...footnoteSx, maxWidth: 'none' }} aria-live="polite">
        {data
          ? `${data.total == null ? 'Unknown total of' : `About ${data.total}`} organizations matching search · includes organizations without intake configuration`
          : 'All organizations, including those without intake configuration'}
      </Typography>
      {track === 'ehr-import' && (
        <Alert severity="info">
          EHR migration applies to the organization. Staff import and verify the uploaded files,
          with progress reported from its import session. Open the Intake Hub to review batches.
        </Alert>
      )}
      {failed && (
        <Alert
          severity={data ? 'warning' : 'error'}
          action={
            <Button color="inherit" onClick={onRefresh}>
              Retry
            </Button>
          }
        >
          {data
            ? 'Refresh failed. Previously loaded evidence remains visible and may be stale.'
            : 'Could not load organizations. Retry to check their stored evidence.'}
        </Alert>
      )}
      {loading && <LinearProgress aria-label="Loading organization evidence" />}
      {data && track === 'credentialing' && (
        <Stack spacing={1.75}>
          {rows.map(row => (
            <CredentialingCard
              key={row.organization_id}
              row={row}
              recordsPath={path(row.organization_id, 'credentialing')}
            />
          ))}
          {emptyState}
          <Typography sx={footnoteSx}>
            One card per practice. The bar is the recorded provider × payer entries: complete, in
            progress, and not started. The practice is complete only when every entry is complete.
          </Typography>
        </Stack>
      )}
      {data && track === 'overview' && (
        <>
          <TableContainer sx={tableWrapSx}>
            <Table aria-label="Staff intake organizations">
              <TableHead>
                <TableRow sx={{ backgroundColor: wf.surfaceAlt }}>
                  <TableCell sx={headCellSx}>Practice</TableCell>
                  <TableCell sx={headCellSx}>Billing</TableCell>
                  <TableCell sx={headCellSx}>Credentialing</TableCell>
                  <TableCell sx={headCellSx}>EHR migration</TableCell>
                  <TableCell sx={headCellSx}>Go live</TableCell>
                  <TableCell sx={headCellSx}>Intake Hub</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const state = credentialingState(row);
                  return (
                    <TableRow key={row.organization_id}>
                      <TableCell sx={bodyCellSx}>
                        <PracticeCell
                          row={row}
                          canVerify={canVerify}
                          verificationPath={path(row.organization_id, 'verification')}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                          <IntakePill
                            tone={row.billing_connection_recorded ? 'green' : 'slate'}
                            label={
                              row.billing_connection_recorded == null
                                ? 'Connection unknown'
                                : row.billing_connection_recorded
                                  ? 'Connection recorded'
                                  : 'Not connected'
                            }
                          />
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'billing')}
                            sx={{ px: 0, textTransform: 'none', fontWeight: 700 }}
                          >
                            Review billing
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                          <IntakePill tone={state.tone} label={state.label} />
                          <Typography sx={metaSx}>
                            {row.credentialing ? (
                              <>
                                {row.credentialing.total ?? 'Unknown'} recorded ·{' '}
                                {row.credentialing.complete ?? 'Unknown'} complete
                              </>
                            ) : (
                              'Credentialing evidence unavailable'
                            )}
                          </Typography>
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'credentialing')}
                            sx={{ px: 0, textTransform: 'none', fontWeight: 700 }}
                          >
                            Review records
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                          <EhrOrganizationStatus organizationId={row.organization_id} />
                          <Button
                            size="small"
                            component={Link}
                            to={path(row.organization_id, 'ehr-import')}
                            sx={{ px: 0, textTransform: 'none', fontWeight: 700 }}
                          >
                            Review uploads
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <GoLiveStatus
                          organizationId={row.organization_id}
                          enabled={goLiveWindow.enabled.has(row.organization_id)}
                          onSettled={goLiveWindow.markSettled}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Button
                          component={Link}
                          to={path(row.organization_id, track)}
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none', borderRadius: '6px' }}
                        >
                          Open Intake Hub
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {empty && emptyState}
          </TableContainer>
          <Typography sx={footnoteSx}>
            Billing reflects whether a payer connection date is recorded. Credentialing counts the
            recorded provider × payer entries. A practice is ready to bill once its first
            payer&rsquo;s Stedi enrollments are all Live.
          </Typography>
        </>
      )}
      {data && track === 'ehr-import' && (
        <>
          <TableContainer sx={tableWrapSx}>
            <Table aria-label="Staff intake organizations">
              <TableHead>
                <TableRow sx={{ backgroundColor: wf.surfaceAlt }}>
                  <TableCell sx={headCellSx}>Practice</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Intake Hub</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.organization_id}>
                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
                        {row.organization_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EhrOrganizationStatus organizationId={row.organization_id} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Button
                        component={Link}
                        to={path(row.organization_id, track)}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'none', borderRadius: '6px' }}
                      >
                        Open Intake Hub
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {empty && emptyState}
          </TableContainer>
          <Typography sx={footnoteSx}>
            Status reflects the uploaded files and their import session.
          </Typography>
        </>
      )}
      <TablePagination
        component="div"
        count={-1}
        page={page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[perPage]}
        onPageChange={(_event, next) => onPage(next + 1)}
        labelRowsPerPage="Organizations per page"
        labelDisplayedRows={() => {
          const size = data?.items.length ?? 0;
          const from = size ? (page - 1) * perPage + 1 : 0;
          const to = size ? (page - 1) * perPage + size : 0;
          return `${from}–${to} of about ${data?.total ?? 0}`;
        }}
        slotProps={{
          actions: {
            nextButton: { disabled: loading || failed || (data?.items.length ?? 0) < perPage },
          },
        }}
      />
    </Stack>
  );
}
